import {
  EtcCard,
  EtcTicketDetailPanel,
  FillQueryResultDialog,
  useFillQueryResultDialog,
  ConfirmPaymentOnBehalfDialog,
  useConfirmPaymentOnBehalfDialog,
  useChargeFailDialog,
  ChargeFailDialog,
  TicketSearchBar,
  TicketSearchBarProps,
  useTicketSearchBarParams,
} from '#/components/ui/tickets/etc/components';
import { CardList } from '../CardList';
import { ExportCompletedTicketButton } from '../exportExcel/ExportCompletedTicketButton';
import { ExportPendingTicketButton } from '../exportExcel/ExportPendingTicketButton';
import { toStartOfDayISO, toEndOfDayISO } from '../../utils';
import { useThrottledValue } from '@mantine/hooks';
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import {
  useChargeTicket,
  useCompleteTicket,
  useEtcTickets,
  useExportEtcPendingTickets,
  useExportCompletedTicket,
  useMarkNoFee,
  useMarkQueryFailed,
} from '#/components/ui/tickets/etc/endpoints/ticketsHooks';
import { useMemo, useState } from 'react';
import { TICKET_TYPE, TICKET_MODE } from '../../config';
import Fuse from 'fuse.js';
import { ListTicketsParams } from '../../etc/endpoints/tickets';
import { useUploadTransactionReceipt } from '../../etc/endpoints/ticketsHooks';
import { TabPanel } from '../TicketsTabbar';
import { ETC_STATE_VALUE } from '../../etc/model';
import { useScheduleEnrollment } from '../../etc/endpoints/enrollmentsHooks';
import { addDays } from 'date-fns';
import { usePanelPaginationParams } from '../usePanelPaginationParams';

export function EtcPanel({
  mode,
  defaultOptions,
  availField,
  availStates = ETC_STATE_VALUE,
}: Partial<TicketSearchBarProps> & { mode: TICKET_MODE }) {
  const isHistoryTicketMode = mode === TICKET_MODE.HISTORY;
  const isPendingTicketMode = mode === TICKET_MODE.PENDING;
  const { params } = useTicketSearchBarParams();

  const queryStates = availStates?.filter((s) => {
    const filterStates = params.state.length === 0 ? availStates : params.state;

    return filterStates.includes(s);
  });

  const ticketsQueryParam: ListTicketsParams = {
    state: queryStates,
    createDateTimeFrom: params.fromDate
      ? toStartOfDayISO(params.fromDate)
      : undefined,
    createDateTimeTo: params.toDate ? toEndOfDayISO(params.toDate) : undefined,
  };

  const { data, isLoading, refetch } = useEtcTickets(ticketsQueryParam);

  const { mutate: markQueryFailed } = useMarkQueryFailed();
  const { mutate: markNoFee } = useMarkNoFee();
  const { mutate: chargeTicket } = useChargeTicket();
  const { mutateAsync: completeTicket } = useCompleteTicket();
  const { mutate: uploadTransactionReceipt } = useUploadTransactionReceipt();
  const { mutateAsync: scheduleEnrollment } = useScheduleEnrollment();
  const { mutate: exportPendingTickets, isPending: isExportingPendingTickets } =
    useExportEtcPendingTickets();
  const {
    mutate: exportCompletedTicket,
    isPending: isExportingCompletedTicket,
  } = useExportCompletedTicket();

  const { props: confirmPaymentOnBehalfDialogProps, confirm: open } =
    useConfirmPaymentOnBehalfDialog();

  const { props: fillQueryResultDialogProps, open: openFillQueryResultDialog } =
    useFillQueryResultDialog();
  const { props: chargeFailDialogProps, open: openChargeFailDialog } =
    useChargeFailDialog();

  const throttledSearchText = useThrottledValue(
    params.fuzzySearchText ?? '',
    1000,
  );
  const etcTickets = data?.data ?? [];

  const searchedResult = useMemo(() => {
    const tickets = data?.data ?? [];
    if (!throttledSearchText.length) {
      return tickets.sort(
        (
          { updateDateTime: updateDateTimeA },
          { updateDateTime: updateDateTimeB },
        ) => updateDateTimeA.localeCompare(updateDateTimeB),
      );
    }

    const fuse = new Fuse(tickets, {
      keys: ['plateNumber', 'idNumber', 'account.email', 'taxIdNumber'],
      threshold: 0.0, // 越小越嚴格（0~1），0.4 通常是合理模糊度
      distance: 100, // 控制字元距離容忍度
      ignoreLocation: true, // 讓 Fuse.js 不那麼在意匹配的位置
      includeScore: true,
    });

    const searched = fuse.search(throttledSearchText ?? '');

    return searched
      .sort(
        ({ score: scoreA }, { score: scoreB }) => (scoreA ?? 0) - (scoreB ?? 0),
      )
      .map(({ item }) => item);
  }, [data, throttledSearchText]);

  const { currentPage, endIndex, setPage, startIndex, totalItems, totalPages } =
    usePanelPaginationParams(searchedResult.length);

  const [currentDetailIndex, setCurrentDetailIndex] = useState<
    number | undefined
  >(undefined);

  const ticket =
    currentDetailIndex === undefined
      ? undefined
      : searchedResult[currentDetailIndex];

  const currentDetailId = ticket?.id;
  const currentDetailPaymentId = ticket?.paymentId || null;

  return (
    <>
      <Box mt={20}>
        <TabPanel tabKey={TICKET_TYPE.ETC_TICKETS}>
          <Stack gap="sm">
            <Paper withBorder bg="gray.1" radius="md" shadow="none" p="sm">
              <TicketSearchBar
                defaultOptions={{
                  ...defaultOptions,
                }}
                availField={availField}
                availStates={availStates}
              />
              <Group gap="xs" mt="xs">
                <Text size="sm" c="dimmed">
                  {data ? `結果共 ${data.extra.totalElement} 筆` : '--'}
                </Text>
                <Tooltip label="重新整理">
                  <ActionIcon
                    variant="subtle"
                    color="dimmed"
                    loading={isLoading}
                    onClick={() => refetch()}
                  >
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Paper>
            <Flex>
              {isPendingTicketMode && (
                <ExportPendingTicketButton
                  loading={isExportingPendingTickets}
                  onExport={() => exportPendingTickets({})}
                />
              )}

              {isHistoryTicketMode && (
                <ExportCompletedTicketButton
                  ticketType={TICKET_TYPE.ETC_TICKETS}
                  loading={isExportingCompletedTicket}
                  onExportSubmit={exportCompletedTicket}
                />
              )}
            </Flex>

            <CardList
              isLoading={isLoading}
              isEmpty={etcTickets.length < 1}
              currentPage={currentPage}
              totalItems={totalItems}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={setPage}
            >
              {searchedResult
                .slice(startIndex, endIndex)
                .map((ticket, index) => (
                  <EtcCard
                    {...ticket}
                    onDetailClick={async () => {
                      setCurrentDetailIndex(startIndex + index);
                    }}
                    onConfirmPaymentClick={async () => {
                      const res = await open(ticket);
                      if (!res) return;

                      try {
                        await completeTicket({
                          id: ticket.id,
                          completeDateTime: new Date().toISOString(),
                        });
                      } catch {
                        return;
                      }

                      if (res.file) {
                        uploadTransactionReceipt({
                          id: ticket.id,
                          file: res.file,
                        });
                      }
                    }}
                    onFillQueryResultClick={async () => {
                      const res = await openFillQueryResultDialog(ticket);

                      if (!res) return;

                      const [result, close] = res;

                      if (!result) return;

                      if (result.type === 'query_failed') {
                        markQueryFailed({
                          id: ticket.id,
                          reason: result.reason,
                        });
                        close();
                        return;
                      }
                      if (result.type === 'no_fee') {
                        markNoFee({ id: ticket.id, reason: '無費用' });
                        close();
                        return;
                      }

                      if (result.type === 'fee_required') {
                        chargeTicket(
                          { id: ticket.id, amount: result.amount },
                          {
                            onSuccess() {
                              close();
                            },

                            async onError(_response, { amount: chargeAmount }) {
                              close();
                              const result = await openChargeFailDialog(
                                ticket,
                                chargeAmount,
                              );

                              if (result && result.afterDays) {
                                const scheduledDate = addDays(
                                  new Date(),
                                  result.afterDays,
                                ).toISOString();

                                await scheduleEnrollment({
                                  scheduledDate,
                                  enrollmentId: ticket.etcAutoPayEnrollmentId,
                                  ticketId: ticket.id,
                                });
                              }
                            },
                          },
                        );
                        return;
                      }
                    }}
                  />
                ))}
            </CardList>
          </Stack>
        </TabPanel>
      </Box>
      <EtcTicketDetailPanel
        id={currentDetailId}
        currentIndex={currentDetailIndex}
        total={searchedResult.length}
        setCurrentDetailIndex={setCurrentDetailIndex}
        paymentId={currentDetailPaymentId}
      />
      <ConfirmPaymentOnBehalfDialog {...confirmPaymentOnBehalfDialogProps} />
      <FillQueryResultDialog {...fillQueryResultDialogProps} />
      <ChargeFailDialog {...chargeFailDialogProps} />
    </>
  );
}
